// Bilingual per-habitat copy for the Mosquito Check guided walk.
//
// PURE data module — no imports (no vue, no pinia, no api client) so both
// SPA bundles can share it:
//   - resident RMO walk: ts/rmo/content/mosquito/* (HabitatCard.vue)
//   - sync staff inspection card: ts/components/CardVirtualInspection.vue
// Keep it dependency-free; district identity tokens ({district_name},
// {district_phone}) in solutions are substituted at render by replaceTokens
// (below), so wording can't hard-code a customer.
//
// Canonical wording source: doc/in-progress/mosquito-check-solutions.md. Habitat wording
// ships in code (not the habitat_type DB rows) so wording edits reach every
// database — migration 00366 drops the seeded text columns.
// Solution copy separates lines with real newlines; renderers show them with
// CSS `white-space: pre-line` (see HabitatCard.vue). The copy is plain text,
// never HTML, so it interpolates safely into any consumer.

export interface HabitatCopyEntry {
	label: { en: string; es: string };
	description: { en: string; es: string };
	solution: { en: string; es: string };
	/** Optional authoritative explainer for this habitat, rendered as a real
	 *  anchor beneath the solution lines (see HabitatCard.vue). Optional so a
	 *  habitat never needs one, and never carries HTML inside `solution` —
	 *  the copy stays plain text and is only ever interpolated as such. */
	link?: {
		href: string;
		label: { en: string; es: string };
	};
}

export const HABITAT_COPY = {
	containers: {
		label: {
			en: "Buckets, bottles, cans & cups",
			es: "Cubetas, botellas, latas y vasos",
		},
		description: {
			en: "Any open container that can collect water — buckets, bottles, cans, cups, jars, or toys.",
			es: "Cualquier recipiente abierto que pueda acumular agua: cubetas, botellas, latas, vasos, frascos o juguetes.",
		},
		solution: {
			en: "Discard all unnecessary containers. Empty all containers and turn them over so they don't hold water.\nDrill small drainage holes in anything you keep outside.\nOnce a week, dump out any water you find and scrub around the water line to remove any mosquito eggs that may have been laid there.",
			es: "Deseche todos los recipientes que no necesite. Vacíe todos los recipientes y voltéelos para que no retengan agua.\nPerfore agujeros pequeños de drenaje en todo lo que conserve afuera.\nUna vez por semana, vacíe cualquier agua que encuentre y restriegue alrededor de la marca del agua para quitar los huevos de mosquito que hayan puesto ahí.",
		},
	},
	plant_saucer: {
		label: {
			en: "Potted plants with saucers",
			es: "Macetas con platillo",
		},
		description: {
			en: "A potted plant with a saucer underneath that holds water — one of the most common mosquito sources. Check every potted plant, indoors and out, and look under the pot.",
			es: "Una maceta con platillo debajo que retiene agua — una de las fuentes de mosquitos más comunes. Revise cada maceta, adentro y afuera, y mire debajo de la maceta.",
		},
		solution: {
			en: "Most plants don't need so much water that a saucer is necessary — you can reduce watering so it doesn't drain through the pot, without harming your plants. If you must have saucers (for certain varieties of plants), any one of these will work:\nDump saucer water every week.\nOr fill saucers with sand or gravel.\nOr use pot feet (small stands that raise the pot above the saucer) that don't hold water.\nOr use a watering bulb (also called a water globe) — it releases water slowly into the soil, so nothing drains into the saucer.\nMake sure pots drain — standing water inside the pot breeds mosquitoes too.",
			es: "La mayoría de las plantas no necesitan tanta agua como para necesitar un platillo: puede reducir el riego para que el agua no drene por la maceta, sin dañar sus plantas. Si aun así necesita platillos (para ciertas variedades de plantas), cualquiera de estas opciones funciona:\nVacíe el agua del platillo cada semana.\nO llene los platillos con arena o grava.\nO use patas para macetas (bases pequeñas que levantan la maceta por encima del platillo) que no retengan agua.\nO use un bulbo de riego (también llamado globo de agua) — libera el agua poco a poco en la tierra, así que nada drena hacia el platillo.\nAsegúrese de que las macetas drenen — el agua estancada dentro de la maceta también cría mosquitos.",
		},
		link: {
			href: "https://ipm.ucanr.edu/home-and-landscape/mosquitoes/",
			label: {
				en: "More on mosquitoes and standing water (UC IPM)",
				es: "Más sobre los mosquitos y el agua estancada (UC IPM)",
			},
		},
	},
	pet_animal_water: {
		label: {
			en: "Pet & animal water",
			es: "Agua para mascotas y animales",
		},
		description: {
			en: "An outdoor bowl or trough kept filled with water for pets or livestock to drink from — pet dishes, horse and cattle troughs, and other animal waterers.",
			es: "Un recipiente o bebedero exterior que se mantiene lleno de agua para que beban las mascotas o los animales de granja: platos para mascotas, bebederos para caballos y ganado, y otros bebederos.",
		},
		solution: {
			en: "Empty and refill bowls with fresh water every day.\nScrub the bowl or trough when you refill it.\nFor large troughs, report it to us — we can add mosquito fish or treat with a pesticide that is safe for your animals.",
			es: "Vacíe los recipientes y llénelos con agua fresca todos los días.\nRestriegue el recipiente o bebedero cada vez que lo rellene.\nEn el caso de los bebederos grandes, avísenos — podemos agregar peces mosquito o tratarlos con un pesticida que sea seguro para sus animales.",
		},
	},
	bird_bath: {
		label: {
			en: "Bird bath",
			es: "Bebedero para pájaros",
		},
		description: {
			en: "A shallow basin kept filled with water to attract birds.",
			es: "Un recipiente poco profundo que se mantiene con agua para atraer aves.",
		},
		solution: {
			en: "Dump and scrub the basin at least once a week.\nAdd a small fountain pump to keep water moving.\nMosquito dunks are safe for birds.",
			es: "Vacíe y restriegue el recipiente al menos una vez por semana.\nAgregue una pequeña bomba de fuente para mantener el agua en movimiento.\nLas pastillas para mosquitos son seguras para las aves.",
		},
	},
	tires: {
		label: {
			en: "Old tires",
			es: "Llantas viejas",
		},
		description: {
			en: "Tires kept outdoors that trap water inside their hollow center.",
			es: "Llantas guardadas afuera que atrapan agua dentro de su centro hueco.",
		},
		solution: {
			en: "Dispose of old tires at a proper facility.\nDrill drainage holes if you keep them.\nCover or store them indoors.",
			es: "Deseche las llantas viejas en un centro adecuado.\nPerfore agujeros de drenaje si las conserva.\nCúbralas o guárdelas bajo techo.",
		},
	},
	tarp_cover: {
		label: {
			en: "Tarp or plastic cover",
			es: "Lona o cubierta de plástico",
		},
		description: {
			en: "A tarp over wood, equipment, or a vehicle where water pools in the folds.",
			es: "Una lona sobre leña, equipo o un vehículo donde el agua se acumula en los pliegues.",
		},
		solution: {
			en: "Pull tarps tight so they have no dips or hollows where water can pool.\nIf water does collect, empty it out.\nCheck that sprinklers are not refilling the tarp.",
			es: "Estire las lonas para que no tengan hundimientos ni huecos donde se pueda acumular el agua.\nSi se acumula agua, vacíela.\nRevise que los aspersores no estén llenando la lona de nuevo.",
		},
	},
	rain_gutter: {
		label: {
			en: "Roof gutter / AC or swamp-cooler drain",
			es: "Canaleta del techo / desagüe de aire acondicionado o enfriador evaporativo",
		},
		description: {
			en: "A roof gutter that stays wet because a swamp cooler or rooftop air conditioner drains into it. In the Central Valley gutters are usually dry because it rarely rains, so this only applies when a cooler or AC unit drains into the gutter.",
			es: "Una canaleta del techo que permanece mojada porque un enfriador evaporativo o un aire acondicionado de techo drena hacia ella. En el Valle Central las canaletas suelen estar secas porque casi no llueve, así que esto solo aplica cuando un enfriador o aire acondicionado drena hacia la canaleta.",
		},
		solution: {
			en: "Keep the drain flowing freely so water does not pool in the gutter.\nClear leaves and debris from the gutter.\nMake sure the cooler or AC drain flows to the ground and leaves no standing water.",
			es: "Mantenga el desagüe fluyendo libremente para que el agua no se acumule en la canaleta.\nQuite hojas y escombros de la canaleta.\nAsegúrese de que el drenaje del enfriador o aire acondicionado fluya hasta el suelo y no deje agua estancada.",
		},
	},
	plant_axil: {
		label: {
			en: "Plant that holds water (bromeliad)",
			es: "Planta que retiene agua (bromelia)",
		},
		description: {
			en: "A plant with a cup or joint that holds water between its leaves.",
			es: "Una planta con una copa o unión que retiene agua entre sus hojas.",
		},
		solution: {
			en: "Ensure that sprinklers do not fill these plants.\nWeekly, apply Bti granules (mosquito bits) to the cups.\nReplace problem plants with ones that don't hold water.",
			es: "Asegúrese de que los aspersores no llenen estas plantas.\nCada semana, aplique gránulos de Bti (mosquito bits) en las copas.\nReemplace las plantas problemáticas por otras que no retengan agua.",
		},
	},
	pool_spa: {
		label: {
			en: "Pool, spa, or hot tub",
			es: "Piscina, spa o jacuzzi",
		},
		description: {
			en: "Any pool, spa, or hot tub on the property — in-ground or above-ground, including kiddie pools and covered tubs. Even maintained pools can hold water on covers.",
			es: "Cualquier piscina, spa o jacuzzi en la propiedad — enterrada o elevada, incluidas las piscinas infantiles y las tinas cubiertas. Incluso las piscinas mantenidas pueden retener agua en las cubiertas.",
		},
		solution: {
			en: "Keep the pump and filtration running.\nRemove standing water from covers.\nKeep the water treated.\nDrain or cover pools and tubs when not in use.\nDump and turn over kiddie pools after each use.\nIf the pool remains unmaintained for more than a week, let us know — we can treat it or add mosquito fish.",
			es: "Mantenga la bomba y la filtración funcionando.\nRetire el agua estancada de las cubiertas.\nMantenga el agua tratada.\nVacíe o cubra las piscinas y las tinas cuando no las use.\nVacíe y voltee las piscinas infantiles después de cada uso.\nSi la piscina queda sin mantenimiento por más de una semana, avísenos — podemos tratarla o agregar peces mosquito.",
		},
	},
	water_feature: {
		label: {
			en: "Fountain, pond, or water feature",
			es: "Fuente, estanque o elemento acuático",
		},
		description: {
			en: "A decorative water feature or backyard pond — especially one whose pump is not running or that has no fish.",
			es: "Un elemento acuático decorativo o un estanque de jardín — sobre todo si su bomba no está funcionando o si no tiene peces.",
		},
		solution: {
			en: "Keep the pump running to move the water.\nAdd mosquito fish — free from {district_name}. Call {district_phone}.\nTreat still water with mosquito dunks (Bti).",
			es: "Mantenga la bomba encendida para mover el agua.\nAgregue peces mosquito — gratuitos de {district_name}. Llame al {district_phone}.\nTrate el agua quieta con pastillas para mosquitos (Bti).",
		},
	},
	low_spots: {
		label: {
			en: "Low spots (yard or pavement)",
			es: "Puntos bajos (jardín o pavimento)",
		},
		description: {
			en: "A low area in dirt, grass, or pavement (including tire ruts and driveways) where water pools.",
			es: "Un área baja en tierra, césped o pavimento (incluidas rodadas y entradas) donde se acumula agua.",
		},
		solution: {
			en: "Fill and grade low spots so water drains.\nSweep or channel water away from paved low spots.\nReduce watering so water does not stand for more than a day.",
			es: "Rellene y nivele los puntos bajos para que drene el agua.\nBarra el agua o diríjala lejos de los puntos bajos pavimentados.\nReduzca el riego para que el agua no se estanque más de un día.",
		},
	},
	yard_drain: {
		label: {
			en: "Yard drain",
			es: "Drenaje de jardín",
		},
		description: {
			en: "A drain set in the lawn or landscape that can trap water.",
			es: "Un desagüe instalado en el césped o jardín que puede atrapar agua.",
		},
		solution: {
			en: "Keep the grate clear of leaves and debris.\nPop the top grate off, place lightweight window screen or mesh over the pipe opening, then push the grate back on.\nCheck the pipe for blockages.\nReduce watering so that it does not enter the drain.\nResidents are welcome to request mesh from the district.",
			es: "Mantenga la rejilla libre de hojas y escombros.\nLevante la rejilla superior, coloque tela mosquitera de ventana o malla liviana sobre la abertura de la tubería y vuelva a colocar la rejilla.\nRevise la tubería por obstrucciones.\nReduzca el riego para que no entre agua al desagüe.\nLos residentes pueden solicitar malla al distrito.",
		},
	},
	appliance: {
		label: {
			en: "Old appliance",
			es: "Electrodoméstico viejo",
		},
		description: {
			en: "A discarded fridge, washer, or freezer that collects water inside.",
			es: "Un refrigerador, lavadora o congelador desechado que acumula agua adentro.",
		},
		solution: {
			en: "Schedule a bulky-item pickup.\nRemove the door or turn it face-down.\nKeep it covered until removal.\nIf water does accumulate (even if it is not visible), please report it so we can add treatments.",
			es: "Programe una recolección de artículos voluminosos.\nQuite la puerta o voltéelo boca abajo.\nManténgalo cubierto hasta que lo retiren.\nSi se acumula agua (aunque no se vea), repórtelo para que podamos aplicar tratamientos.",
		},
	},
	utility: {
		label: {
			en: "Valve / meter box",
			es: "Caja de válvula / medidor",
		},
		description: {
			en: "A valve or meter box in the yard that can collect water.",
			es: "Una caja de válvula o medidor en el jardín que puede acumular agua.",
		},
		solution: {
			en: "Keep the lid sealed.\nRemove standing water inside.\nReport broken or unsealed boxes.\nFix leaking pipes.\nReduce lawn watering so that it does not fill utility boxes.",
			es: "Mantenga la tapa sellada.\nRetire el agua estancada del interior.\nReporte las cajas rotas o sin sello.\nRepare las tuberías con fugas.\nReduzca el riego del césped para que el agua no llene las cajas de válvulas o medidores.",
		},
	},
	misc_water: {
		label: {
			en: "Anything else holding water",
			es: "Cualquier otra cosa que retenga agua",
		},
		description: {
			en: "Any other standing water on your property not listed above.",
			es: "Cualquier otra agua estancada en su propiedad no mencionada arriba.",
		},
		solution: {
			en: "Dump it, cover it, or turn it over.\nIf you're unsure how to fix it, {district_name} can help.",
			es: "Vacíelo, cúbralo o voltéelo.\nSi no sabe cómo resolverlo, {district_name} puede ayudarle.",
		},
	},
} as const;

export type HabitatKey = keyof typeof HABITAT_COPY;

// Returns bilingual copy for a habitat key, or undefined for keys without a
// taxonomy entry (unknown keys keep a raw-key fallback in the UI).
export function habitatCopy(key: string): HabitatCopyEntry | undefined {
	return HABITAT_COPY[key as HabitatKey];
}

// Convenience accessor returning just the label pair ({ en, es }).
export function habitatLabel(
	key: string,
): { en: string; es: string } | undefined {
	return HABITAT_COPY[key as HabitatKey]?.label;
}

// Runtime org identity injected into solution copy at render (resident state
// payload org_name/phone_office — never hard-coded in this module).
export interface HabitatOrg {
	district_name: string;
	district_phone: string;
}

// Pure substitution of {district_name}/{district_phone} tokens in solution
// copy. Callers decide values: the resident walk substitutes the org fields
// (with a neutral name fallback) so wording stays grammatical; empty values
// substitute to "".
export function replaceTokens(text: string, org: HabitatOrg): string {
	return text.replace(/\{district_name\}|\{district_phone\}/g, (match) =>
		match === "{district_name}" ? org.district_name : org.district_phone,
	);
}
